import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { ChevronRightIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';

import FullScreenChartOverlay from '../../../../components/FullScreenChartOverlay';

import AnalyticsHero from '../../../../components/analytics/AnalyticsHero';
import AnalyticsStatStrip from '../../../../components/analytics/AnalyticsStatStrip';
import DailyScoreChart from '../../../../components/analytics/DailyScoreChart';
import { useExcludedDays } from '../../../../contexts/ExcludedDaysContext';
import PeerLeaderboardRail from '../../../../components/analytics/PeerLeaderboardRail';
import AnimationCircle from '../../../../components/AnimationCircle';
import {
  selectMonthlyStatsData,
  selectMonthlyStatsLoading,
} from '../../../../slices/reportsSlice';

const card =
  'bg-surface dark:bg-gray-870 rounded-xl p-4 border border-gray-200/70 dark:border-gray-600/70 shadow-sm';

// Phone-only glance view for /analytics (forecaster role). One scroll:
// month-rank hero, four stat tiles, the daily score chart, the peer
// leaderboard, and payments behind a collapsible section. Reuses the same
// monthly-stats payload the Ranking/Errors tabs consume; Reports.js keeps
// that fetch warm on phones regardless of the (hidden) active tab.
export default function MobileAnalyticsSummary({
  selfUserId,
  year,
  month,
  metricLabel = 'Score',
  blurSensitive = false,
  children, // payments section content, mounted only while expanded
}) {
  const monthlyStatsData = useSelector(selectMonthlyStatsData);
  const loading = useSelector(selectMonthlyStatsLoading);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [chartExpanded, setChartExpanded] = useState(false);

  const monthMoment = useMemo(
    () => moment({ year, month: (month || 1) - 1 }),
    [year, month]
  );
  const monthLabel = monthMoment.format('MMMM');
  const daysInPeriod = monthMoment.daysInMonth();

  // Score-sorted peer board; unqualified rows keep their position but get no
  // rank slot (mirrors ErrorsReport's derivation so numbers match desktop).
  const peerRows = useMemo(() => {
    const raw = monthlyStatsData?.month_scores_json;
    if (!Array.isArray(raw)) return [];
    let qualifiedRank = 0;
    return [...raw]
      .filter((row) => row && row.score != null)
      .sort((a, b) => Number(a.score) - Number(b.score))
      .map((row) => {
        const unqualified = row.is_unqualified === true;
        return {
          ...row,
          rank: unqualified ? null : ++qualifiedRank,
          delta: 0,
          unqualified,
        };
      });
  }, [monthlyStatsData]);

  const selfRow = useMemo(
    () => peerRows.find((r) => r.user_id === selfUserId) || null,
    [peerRows, selfUserId]
  );

  // Outage days of the shown horizon: neither submitted nor expected, and
  // hatched and empty in the daily chart.
  const excludedDays = useExcludedDays(monthlyStatsData?.horizon_offset, year, month);

  // The engine drops excluded days when it rebuilds the month; filtering
  // here also covers the hour before that rebuild.
  const dailyRanks = useMemo(() => {
    const raw = monthlyStatsData?.daily_ranks_json;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((d) => d && d.rank != null && d.target_day && !excludedDays.has(d.target_day))
      .sort((a, b) => String(a.target_day).localeCompare(String(b.target_day)));
  }, [monthlyStatsData, excludedDays]);

  const dailyScores = useMemo(() => {
    const raw = monthlyStatsData?.daily_scores_json;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((d) => ({
      date: d.target_day,
      value: d.score,
      isPenalty: !!d.is_penalty,
    }));
  }, [monthlyStatsData]);

  const hasData = monthlyStatsData && monthlyStatsData.user_id === selfUserId;

  if (loading && !hasData) {
    return (
      <div className="flex items-center justify-center py-16">
        <AnimationCircle />
      </div>
    );
  }

  if (!hasData || (peerRows.length === 0 && dailyRanks.length === 0)) {
    return (
      <div className={`${card} text-sm text-gray-500 dark:text-gray-400`}>
        No evaluation data for {monthLabel} {year} yet. Scores appear after the
        first settled market session of the month.
      </div>
    );
  }

  const latest = dailyRanks[dailyRanks.length - 1] || null;
  const submitted = dailyRanks.length;
  const expectedDays = daysInPeriod - excludedDays.size;
  const strip = [
    {
      label: 'Monthly rank',
      value: selfRow?.rank != null ? `#${selfRow.rank}` : '—',
      subtext: `of ${peerRows.length} forecasters`,
    },
    {
      label: `Avg ${metricLabel}`,
      value: selfRow?.score != null ? Number(selfRow.score).toFixed(2) : '—',
      subtext: `${monthLabel} average`,
    },
    {
      label: 'Submitted',
      value: `${submitted}/${expectedDays}`,
      subtext: `${expectedDays ? Math.round((submitted / expectedDays) * 100) : 0}% of days`,
    },
    {
      label: 'Latest daily rank',
      value: latest ? `#${latest.rank}` : '—',
      subtext: latest ? moment(latest.target_day).format('MMM D') : 'no days yet',
    },
  ];

  return (
    <div className="space-y-4">
      <AnalyticsHero
        eyebrow={`Analytics · ${monthLabel} ${year}`}
        headline={
          selfRow?.rank != null ? (
            <>
              You&rsquo;re{' '}
              <span className="italic font-semibold text-brand-600 dark:text-brand-400">
                #{selfRow.rank}
              </span>{' '}
              of {peerRows.length} this month.
            </>
          ) : (
            <>Not ranked yet this month.</>
          )
        }
        lede={`Based on your ${metricLabel} across ${submitted} submitted day${
          submitted === 1 ? '' : 's'
        }.`}
      />

      <AnalyticsStatStrip cells={strip} />

      {/* DailyScoreChart and PeerLeaderboardRail bring their own card chrome
          and headings, so they render bare here (no wrapper card). */}
      {dailyScores.length > 0 && (
        <div>
          <DailyScoreChart
            dailyScores={dailyScores}
            metricLabel={metricLabel}
            monthLabel={monthLabel}
            excludedDays={excludedDays}
          />
          <button
            type="button"
            onClick={() => setChartExpanded(true)}
            className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg border border-gray-200/70 dark:border-gray-600/70 py-2 text-xs font-semibold text-brand-600 dark:text-brand-400"
          >
            <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden="true" />
            Expand chart
          </button>
          <FullScreenChartOverlay
            open={chartExpanded}
            onClose={() => setChartExpanded(false)}
            title={`Daily ${metricLabel} · ${monthLabel}`}
          >
            <DailyScoreChart
              dailyScores={dailyScores}
              metricLabel={metricLabel}
              monthLabel={monthLabel}
              excludedDays={excludedDays}
              fill
            />
          </FullScreenChartOverlay>
        </div>
      )}

      <PeerLeaderboardRail
        rows={peerRows}
        selfUserId={selfUserId}
        mode="errors"
        metricLabel={metricLabel}
        maxMissedDays={monthlyStatsData?.max_missed_days ?? 5}
        blurSensitive={blurSensitive}
      />

      <div className={card}>
        <button
          type="button"
          onClick={() => setPaymentsOpen((v) => !v)}
          aria-expanded={paymentsOpen}
          className="w-full flex items-center justify-between text-sm font-bold text-gray-900 dark:text-gray-100"
        >
          Payments
          <ChevronRightIcon
            className={`h-4 w-4 text-gray-400 transition-transform ${paymentsOpen ? 'rotate-90' : ''}`}
          />
        </button>
        {paymentsOpen && <div className="mt-3">{children}</div>}
      </div>
    </div>
  );
}
